import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface HostBrand {
  name: string;
  logoUrl: string | null;
  website: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  fontFamily: string | null;
  footerText: string | null;
  hidePoweredBy?: boolean;
  loginBgColor?: string | null;
  loginHeading?: string | null;
  loginSubtext?: string | null;
  loginButtonLabel?: string | null;
  accentSecondary?: boolean;
  loginStyle?: 'flat' | 'glass';
}

/** The active host brand (set once resolved), so any component can read it
 *  synchronously without prop-drilling. null on the main domain. */
export let activeHostBrand: HostBrand | null = null;
export function isBrandedHost(): string | null {
  if (typeof window === 'undefined') return null;
  const h = window.location.hostname;
  if (h === 'todaidesign.net' || h === 'www.todaidesign.net' || h === 'localhost' ||
      h === '127.0.0.1' || h.endsWith('.local')) return null;
  // Preview/demo hosts (cloudflared quick tunnels, Netlify Drop) behave like
  // the main domain — otherwise a shared preview link renders as a white-label
  // customer host: forced-light theme, no cinematic shell, /auth redirect.
  if (h.endsWith('.trycloudflare.com') || h.endsWith('.netlify.app') || h.endsWith('.pages.dev')) return null;
  // A branded customer host is always a real domain name — never a bare IP.
  // LAN dev access (e.g. 192.168.x.x) and IPv6 literals must behave like
  // localhost, not trigger the branded-host guard.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h) || h.includes(':')) return null;
  return h;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Reactive accessor for the active host brand. Returns null on the main domain;
 *  the brand object on a customer's branded host (re-renders once resolved).
 *  Surfaces use it to swap the TodAidesign logo/name for the customer's. */
export function useHostBranding(): HostBrand | null {
  const [brand, setBrand] = useState<HostBrand | null>(activeHostBrand);
  useEffect(() => {
    if (activeHostBrand) setBrand(activeHostBrand);
    const on = (e: Event) => setBrand((e as CustomEvent).detail as HostBrand);
    window.addEventListener('host-branding', on);
    return () => window.removeEventListener('host-branding', on);
  }, []);
  return brand;
}

/**
 * White-labels the WHOLE app on a customer's branded host (portal.firm.com or
 * firm.todaidesign.net): swaps the primary colour, page title and favicon to the
 * owner's brand. Mounted once in RootLayout; a no-op on the main domain.
 * (Logo-in-header / login copy / hiding our name read `activeHostBrand` + the
 *  `host-branding` event — wired per-surface as the white-label sweep lands.)
 */
export default function HostBranding() {
  useEffect(() => {
    const host = isBrandedHost();
    if (!host) return;
    let cancelled = false;
    (async () => {
      try {
        const url = import.meta.env.VITE_SUPABASE_URL;
        const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(`${url}/functions/v1/client-portal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey, Authorization: `Bearer ${apikey}` },
          body: JSON.stringify({ action: 'host-branding', host }),
        });
        const data = await res.json().catch(() => ({}));
        const b: HostBrand | null = data?.branding ?? null;
        if (cancelled || !b) return;

        activeHostBrand = b;
        const root = document.documentElement;
        if (b.primaryColor && HEX.test(b.primaryColor)) {
          root.style.setProperty('--color-brand-blue', b.primaryColor);
          root.style.setProperty('--color-brand-blue-hover', b.primaryColor);
        }
        if (b.name) document.title = b.name;
        if (b.logoUrl) {
          for (const sel of ["link[rel='icon']", "link[rel='apple-touch-icon']", "link[rel='mask-icon']"]) {
            document.querySelectorAll(sel).forEach((el) => ((el as HTMLLinkElement).href = b.logoUrl!));
          }
          let icon = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
          if (!icon) { icon = document.createElement('link'); icon.rel = 'icon'; document.head.appendChild(icon); icon.href = b.logoUrl; }
        }
        // Let host-aware surfaces (header logo, footer, login) re-render with it.
        window.dispatchEvent(new CustomEvent('host-branding', { detail: b }));
      } catch { /* main-domain default branding on any failure */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return null;
}
